/**
 * waitFor
 *
 * Higher-order component that blocks rendering the component until a
 * condition is satisfied. It takes `props` as an argument.
 */
import * as React from 'react';

import PropTypes from 'prop-types';
import type { LegacyContextType } from 'types/legacy-context-types';

import hoistNonReactStatics from 'js/lib/hoistNonReactStatics';

export default function waitFor<Props extends {} = any>(
  // TypeScript does not have a "truthy" type
  conditionFn: (props: Props) => any,
  loadingComponent: React.ReactNode | React.FC = null
) {
  return function (Component: React.ComponentType<Props>): React.ComponentClass<Props> {
    const componentName = Component.displayName || Component.name;

    class WaitForWrapper extends React.Component<Props> {
      static displayName = componentName + 'WaitForWrapper';

      static contextTypes = {
        router: PropTypes.object,
      };

      declare context: LegacyContextType<typeof WaitForWrapper.contextTypes>;

      render() {
        if (!conditionFn(this.props)) {
          if (typeof loadingComponent === 'function') {
            return loadingComponent(this.props, this.context);
          } else {
            return loadingComponent;
          }
        }

        return React.createElement(Component, Object.assign({}, this.props));
      }
    }

    hoistNonReactStatics(WaitForWrapper, Component);

    return WaitForWrapper;
  };
}
